<template>
  <section class="data-visualization-kpi-card" :class="{ fullscreen: fullscreen }">
    <KPICardHeader
      :title="title"
      :presentInfo="false"
      :hasInfo="info !== undefined"
      @onChange="(value) => (presentInfo = value)"
    />
    <KPICardBody
      :presentDetails="presentDetails"
      :hasDetails="!!kpiDetails"
      :showOnMapEnabled="showOnMapEnabledComplete"
      :showOnMap="showOnMapComplete"
      :presentInfo="presentInfo"
      :color="color"
      :isLoading="isLoading || (dataSource !== undefined && dataSource.isLoading)"
      :kpi="value"
      :imgSrc="imgSrc"
      :format="kpiFormat"
      :information="info"
      @toggleDetails="() => (presentDetails = !presentDetails)"
      @closeInfo="() => (presentInfo = false)"
      @toggleShowOnMap="
        () => {
          emits('toggleShowOnMap')
          if (dataSource === undefined) onToggleShowOnMap()
        }
      "
    />
    <KPICardFooter
      v-if="kpiDetails"
      :presentDetails="presentDetails"
      :header="kpiDetails.header"
      :rows="kpiDetails.rows"
    />
    <FeatureComponent
      v-if="presentLegend"
      :colorScale="colorScale"
      :mean="value !== undefined && typeof value === 'number' ? value : 0"
      :format="kpiFormat"
      feature="home/analytics/*/*/colorlegend"
      :title="title"
    />
  </section>
</template>

<script lang="ts" setup>
import KPICardHeader from './KPICardHeader.vue'
import KPICardBody from './KPICardBody.vue'
import KPICardFooter from './KPICardFooter.vue'
import { computed, inject, onUnmounted, ref, watch } from 'vue'
import { FormatOptions, formatter } from '../../formatter'
import { useDataSource } from '@/composables/useDataSources'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { KpiResult } from '@/api-client/models/indoorclimate/Contracts'
import useColoredEntities from '@/composables/useColoredEntities'
import { ColorScaleConfig, getColorForValue } from '@/utils/colorScale'
import { useI18n } from 'vue-i18n'
import FeatureComponent from '@/components/FeatureComponent.vue'
import md5 from 'md5'
import { useCurrentFeatureFilters } from '@/composables/useFilters'
import { sharedDataKey } from '../../../types'
import { Feature } from '@/pinia/feature'

export interface Props {
  fullscreen: boolean
  title: string
  color?: string
  info?: string
  kpi?: number | string
  dataSource?: string
  colorScale?: ColorScaleConfig | string
  imgSrc?: string
  kpiFormat?: FormatOptions
  metadataFormat?: Record<string, FormatOptions>
  metadataLabels?: Record<string, LocaleString>
  numberOfSpaces?: number
  isLoading: boolean
  first?: boolean
  feature?: Feature
  kpiDetails?: {
    header: string[]
    rows: any[][]
  }
  showOnMap: boolean
  showOnMapEnabled: boolean
  kpiType: string
}

type Emits = {
  (e: 'toggleShowOnMap'): void
}

export interface GroupedData {
  value: number
  numberOfSpaces: number
  roomGroup: string | undefined
}

const props = withDefaults(defineProps<Props>(), {
  kpi: undefined,
  color: undefined,
  kpiDetails: undefined,
  imgSrc: undefined,
  dataSource: undefined,
  info: undefined,
  colorScale: undefined,
  metadataFormat: undefined,
  metadataLabels: undefined,
  numberOfSpaces: undefined,
  feature: undefined,
  kpiFormat: (): FormatOptions => ({})
})

const emits = defineEmits<Emits>()

const { locale } = useI18n()

const filters = useCurrentFeatureFilters()

const presentInfo = ref<boolean>(false)
const presentDetails = ref<boolean>(false)

const colorScaleId = computed(() =>
  typeof props.colorScale === 'string' ? props.colorScale : undefined
)
const dataSourceId = computed(() => props.dataSource)
const sharedData = inject(sharedDataKey)
const fullscreen = computed(() => {
  return props.fullscreen || (sharedData?.fullscreen ?? false)
})

const dataSource = useDataSource<KpiResult[]>(dataSourceId)
const colorScaleDs = useDataSource<ColorScaleConfig>(colorScaleId)
const colorScale = computed<ColorScaleConfig | undefined>(() => {
  if (colorScaleDs.value?.data) return colorScaleDs.value.data
  if (typeof props.colorScale === 'object') return props.colorScale
  return undefined
})

const colorScaleColor = computed(() =>
  colorScale.value && typeof value.value === 'number'
    ? getColorForValue(colorScale.value, value.value).toHex()
    : '#000'
)

const color = computed(() => props.color ?? colorScaleColor.value)

const coloredEntities = useColoredEntities()

const value = computed(() => {
  let result = props.kpi ?? dataSource.value?.data?.[0]?.values?.[0]?.value
  if (props.kpiType === 'MAX_REMAINDER_CAPACITY' && props.numberOfSpaces === 1) {
    return undefined
  }
  if (result === null) {
    result = undefined
  }
  return result
})

const key = computed<string>(() => md5(props.title + props.feature?.name))

const showOnMapComplete = computed(
  () =>
    props.showOnMap ||
    (coloredEntities.currentKey !== undefined && coloredEntities.currentKey === key.value)
)

const showOnMapEnabledComplete = computed(() => {
  return props.showOnMapEnabled
})

const presentLegend = computed(() => {
  return [showOnMapComplete.value, showOnMapEnabledComplete.value, !fullscreen.value].every(
    (v) => v === true
  )
})

onUnmounted(() => {
  coloredEntities.reset()
})

watch(
  [dataSource, key],
  () => {
    if (props.first) {
      onToggleShowOnMap()
    }
  },
  { immediate: true }
)

function formatKpi(value: number | string) {
  if (typeof props.kpiFormat === 'function') {
    // return props.kpiFormat(value)
  }
  return formatter(value, locale.value, props.kpiFormat)
}

function onToggleShowOnMap() {
  if (
    dataSource.value &&
    dataSource.value.data &&
    colorScale.value &&
    props.colorScale &&
    filters.value?.submittedMeta.entityType !== undefined &&
    ['SPACE', 'LEVEL', 'REAL_ESTATE', 'COMPLEX', 'CONSTRUCTION'].includes(
      filters.value.submittedMeta.entityType
    )
  ) {
    const data = dataSource.value.data[1].values
      .map((v) => ({
        id: v.splitKey,
        meta: Object.fromEntries(
          Object.entries(v.numberOf)
            .map(([k, v]) => [
              props.metadataLabels?.[k.toLocaleLowerCase()]?.[locale.value],
              formatter(v, locale.value, props.metadataFormat?.[k])
            ])
            .filter(([k, v]) => k && v)
        ),
        value: v.value
      }))
      .filter(
        (v) => v.id !== undefined && v.id !== null && v.value !== undefined && v.value !== null
      )
    coloredEntities.reset()
    coloredEntities.set({
      key: key.value,
      data,
      colorScale: colorScale.value,
      title: props.title,
      unit: '',
      formatter: formatKpi
    })
  } else {
    coloredEntities.reset()
  }
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.data-visualization-kpi-card {
  width: calc(50% - 8px);
  height: fit-content;
  padding: 16px;
  gap: 8px;
  //border: 1px solid transparent;
  border-radius: 8px;
  flex-shrink: 1;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
  background-color: variables.$color-pure-white;
  &.fullscreen {
    width: calc(25% - 12px);
  }
}

@media screen and (max-width: 1080px) {
  .data-visualization-kpi-card {
    &.fullscreen {
      width: calc(50% - 8px);
    }
  }
}

@media screen and (max-width: 580px) {
  .data-visualization-kpi-card {
    &.fullscreen {
      width: calc(100%);
    }
  }
}

@media screen and (max-width: 400px) {
  .data-visualization-kpi-card {
    width: 100%;
  }
}
</style>
